import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Book,
  Users,
  Calendar,
  MessageCircle,
  Target,
  User,
  Settings,
  Shield,
} from "lucide-react";
import { UserPersona } from "@/types/user";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { useUser } from "@/contexts/UserContext";

type GuideSection = {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: React.ReactNode;
};

export default function UserGuide({ onClose }: { onClose: () => void }) {
  const [activeTab, setActiveTab] = useState("overview");
  const { user } = useUser();
  const userPersona = user?.persona || "individual";

  // Define guide sections based on the user's persona
  const guideSections: GuideSection[] = [
    {
      id: "overview",
      title: "Platform Overview",
      icon: <Book className="h-4 w-4" />,
      content: <OverviewGuide />,
    },
    {
      id: "check-ins",
      title: "Daily Check-ins",
      icon: <Calendar className="h-4 w-4" />,
      content: <CheckInsGuide />,
    },
    {
      id: "chat",
      title: "Team Chat",
      icon: <MessageCircle className="h-4 w-4" />,
      content: <ChatGuide />,
    },
    {
      id: "goals",
      title: "Goal Tracking",
      icon: <Target className="h-4 w-4" />,
      content: <GoalsGuide />,
    },
    {
      id: "teams",
      title: "Teams",
      icon: <Users className="h-4 w-4" />,
      content: <TeamsGuide persona={userPersona} />,
    },
    {
      id: "roles",
      title: "Roles & Permissions",
      icon: <Shield className="h-4 w-4" />,
      content: <RolesGuide userPersona={userPersona} />,
    },
    {
      id: "calendar",
      title: "Calendar Sync",
      icon: <Calendar className="h-4 w-4" />,
      content: <CalendarGuide />,
    },
  ];

  return (
    <Card className="w-full max-w-4xl mx-auto overflow-hidden">
      <CardHeader className="bg-muted/50 pb-4">
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-2xl">Step User Guide</CardTitle>
            <CardDescription>
              Learn how to make the most of the platform
            </CardDescription>
          </div>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Tabs
          defaultValue="overview"
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full"
        >
          <div className="flex">
            <div className="w-64 border-r min-h-[550px]">
              <TabsList className="flex flex-col h-auto p-0 bg-transparent w-full rounded-none">
                {guideSections.map((section) => (
                  <TabsTrigger
                    key={section.id}
                    value={section.id}
                    className="justify-start gap-3 p-4 rounded-none border-b data-[state=active]:border-l-4 data-[state=active]:border-l-primary data-[state=active]:bg-accent/50"
                  >
                    <span className="flex items-center">
                      {section.icon}
                      <span className="ml-3">{section.title}</span>
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            <div className="flex-1 p-6 overflow-y-auto max-h-[550px]">
              {guideSections.map((section) => (
                <TabsContent
                  key={section.id}
                  value={section.id}
                  className="m-0"
                >
                  {section.content}
                </TabsContent>
              ))}
            </div>
          </div>
        </Tabs>
      </CardContent>
    </Card>
  );
}

// Platform Overview Guide
function OverviewGuide() {
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Welcome to Step</h2>
      <p className="text-muted-foreground">
        Step is a platform designed to help teams collaborate effectively, track
        wellbeing, and reach goals together.
      </p>

      <div className="space-y-3 mt-4">
        <h3 className="text-lg font-medium">Core Features</h3>
        <ul className="space-y-2 list-disc pl-5">
          <li>
            <strong>Daily Check-ins:</strong> Track your mood, energy, and focus
            levels daily.
          </li>
          <li>
            <strong>Team Collaboration:</strong> Connect with team members
            through chat and see their status.
          </li>
          <li>
            <strong>Goal Tracking:</strong> Set and monitor progress on personal
            and team goals.
          </li>
          <li>
            <strong>Calendar Integration:</strong> Sync with your preferred
            calendar to schedule focus time.
          </li>
          <li>
            <strong>Insights:</strong> Gain valuable insights on your wellbeing
            trends and team dynamics.
          </li>
        </ul>
      </div>

      <div className="space-y-3 mt-4">
        <h3 className="text-lg font-medium">Getting Started</h3>
        <ol className="space-y-2 list-decimal pl-5">
          <li>
            Complete your <strong>Daily Check-in</strong> each day to track your
            wellbeing metrics.
          </li>
          <li>
            View your team's status on the <strong>Dashboard</strong>.
          </li>
          <li>
            Use the <strong>Chat</strong> feature to connect with teammates.
          </li>
          <li>
            Set up <strong>Goals</strong> to track your progress.
          </li>
          <li>
            Connect your <strong>Calendar</strong> to schedule focus time and
            avoid conflicts.
          </li>
        </ol>
      </div>

      <Separator className="my-4" />

      <p className="text-sm text-muted-foreground">
        Use the navigation on the left to learn more about each feature in
        detail.
      </p>
    </div>
  );
}

// Daily Check-ins Guide
function CheckInsGuide() {
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Daily Check-ins</h2>
      <p className="text-muted-foreground">
        Daily check-ins help you track your wellbeing metrics and reflect on
        your workday.
      </p>

      <div className="space-y-3 mt-4">
        <h3 className="text-lg font-medium">How to Complete a Check-in</h3>
        <ol className="space-y-2 list-decimal pl-5">
          <li>
            Navigate to the <strong>Check-in</strong> page from the sidebar or
            dashboard.
          </li>
          <li>
            Rate your <strong>Mood</strong> on the 1-5 scale.
          </li>
          <li>
            Use the sliders to indicate your <strong>Energy</strong> and{" "}
            <strong>Focus</strong> levels.
          </li>
          <li>
            List your <strong>Tasks</strong> for the day.
          </li>
          <li>
            Note any <strong>Blockers</strong> you're facing.
          </li>
          <li>
            Share any <strong>Wins</strong> you've achieved.
          </li>
          <li>
            Add a <strong>Reflection</strong> about your day.
          </li>
          <li>Indicate if you need help with anything.</li>
          <li>Submit your check-in to record it.</li>
        </ol>
      </div>

      <div className="space-y-3 mt-4">
        <h3 className="text-lg font-medium">Benefits of Regular Check-ins</h3>
        <ul className="space-y-2 list-disc pl-5">
          <li>Track patterns in your energy and focus over time.</li>
          <li>Identify factors that affect your productivity.</li>
          <li>Receive personalized action suggestions based on your state.</li>
          <li>Help team leads understand how to support you better.</li>
          <li>Build self-awareness about your work patterns.</li>
        </ul>
      </div>

      <div className="p-4 bg-muted rounded-md mt-4">
        <p className="text-sm font-medium">Pro Tip</p>
        <p className="text-sm text-muted-foreground">
          Try to complete your check-in at the same time each day for the most
          accurate tracking of your patterns.
        </p>
      </div>
    </div>
  );
}

// Team Chat Guide
function ChatGuide() {
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Team Chat</h2>
      <p className="text-muted-foreground">
        Connect with your teammates through individual and group chats.
      </p>

      <div className="space-y-3 mt-4">
        <h3 className="text-lg font-medium">Using the Chat Feature</h3>
        <ol className="space-y-2 list-decimal pl-5">
          <li>
            Access the chat by clicking on the <strong>Chat</strong> icon in the
            sidebar.
          </li>
          <li>View your existing conversations in the left panel.</li>
          <li>
            Start a new conversation by clicking the <strong>New Chat</strong>{" "}
            button.
          </li>
          <li>
            Create group chats by clicking <strong>New Group</strong> and
            selecting team members.
          </li>
          <li>Send nudges to team members who haven't responded in a while.</li>
        </ol>
      </div>

      <div className="space-y-3 mt-4">
        <h3 className="text-lg font-medium">Chat Notifications</h3>
        <ul className="space-y-2 list-disc pl-5">
          <li>Receive real-time notifications when you get new messages.</li>
          <li>See unread message counts on the chat icon.</li>
          <li>Configure notification preferences in your settings.</li>
        </ul>
      </div>

      <div className="space-y-3 mt-4">
        <h3 className="text-lg font-medium">
          Team Communication Best Practices
        </h3>
        <ul className="space-y-2 list-disc pl-5">
          <li>Use group chats for team-wide announcements and discussions.</li>
          <li>Send direct messages for one-on-one conversations.</li>
          <li>Use the nudge feature respectfully when you need a response.</li>
          <li>Set your status to let others know your availability.</li>
        </ul>
      </div>
    </div>
  );
}

// Goal Tracking Guide
function GoalsGuide() {
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Goal Tracking</h2>
      <p className="text-muted-foreground">
        Set, track, and achieve your personal and professional goals.
      </p>

      <div className="space-y-3 mt-4">
        <h3 className="text-lg font-medium">Creating Goals</h3>
        <ol className="space-y-2 list-decimal pl-5">
          <li>
            Navigate to your <strong>Profile</strong> or{" "}
            <strong>My Growth</strong> page.
          </li>
          <li>
            <strong>Add New Goal</strong> button.
          </li>
          <li>Enter a title and description for your goal.</li>
          <li>
            Select a category (e.g., Professional Development, Wellbeing).
          </li>
          <li>Set a target date if applicable.</li>
          <li>Add milestones to break down your goal into manageable steps.</li>
          <li>Save your goal to start tracking progress.</li>
        </ol>
      </div>

      <div className="space-y-3 mt-4">
        <h3 className="text-lg font-medium">Tracking Progress</h3>
        <ul className="space-y-2 list-disc pl-5">
          <li>
            Update your goal progress regularly using the progress slider.
          </li>
          <li>Mark milestones as complete as you achieve them.</li>
          <li>Add reflections to document your journey.</li>
          <li>View progress trends in your insights dashboard.</li>
        </ul>
      </div>

      <div className="space-y-3 mt-4">
        <h3 className="text-lg font-medium">Team Goals</h3>
        <p>If you're a team manager:</p>
        <ul className="space-y-2 list-disc pl-5">
          <li>
            Create team-wide goals that everyone can see and contribute to.
          </li>
          <li>Assign specific team members to goals or milestones.</li>
          <li>Monitor overall team progress through the dashboard.</li>
          <li>
            Celebrate achievements with the team when goals are completed.
          </li>
        </ul>
      </div>

      <div className="p-4 bg-muted rounded-md mt-4">
        <p className="text-sm font-medium">Pro Tip</p>
        <p className="text-sm text-muted-foreground">
          The most effective goals are SMART: Specific, Measurable, Achievable,
          Relevant, and Time-bound.
        </p>
      </div>
    </div>
  );
}

// Teams Guide
function TeamsGuide({ persona }: { persona: UserPersona }) {
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Teams</h2>
      <p className="text-muted-foreground">
        Connect with your colleagues and collaborate more effectively as a team.
      </p>

      <div className="space-y-3 mt-4">
        <h3 className="text-lg font-medium">Team Dashboard</h3>
        <ul className="space-y-2 list-disc pl-5">
          <li>View your team members and their current status.</li>
          <li>See who's online and their recent check-in status.</li>
          <li>Quickly start chats or send nudges from the team view.</li>
          <li>Monitor team energy levels and identify potential issues.</li>
        </ul>
      </div>

      {(persona === "manager" || persona === "admin") && (
        <div className="space-y-3 mt-4">
          <h3 className="text-lg font-medium">Managing Teams</h3>
          <ol className="space-y-2 list-decimal pl-5">
            <li>
              Invite new members using the <strong>Team Invite</strong> button.
            </li>
            <li>
              Generate and share invitation links that are valid for 7 days by
              default.
            </li>
            <li>
              Monitor team insights to identify trends and areas for
              improvement.
            </li>
            <li>Set team-wide goals and track collective progress.</li>
            <li>Organize team focus sessions and check-ins.</li>
          </ol>
        </div>
      )}

      <div className="space-y-3 mt-4">
        <h3 className="text-lg font-medium">Joining a Team</h3>
        <ol className="space-y-2 list-decimal pl-5">
          <li>Receive an invitation link from your team administrator.</li>
          <li>Click the link to join the team automatically.</li>
          <li>
            After joining, you'll have access to all team resources and
            communications.
          </li>
        </ol>
      </div>

      <div className="space-y-3 mt-4">
        <h3 className="text-lg font-medium">Team Communication</h3>
        <ul className="space-y-2 list-disc pl-5">
          <li>Use group chats for team-wide discussions.</li>
          <li>
            Share updates through check-ins that are visible to your team.
          </li>
          <li>Request help from teammates when facing challenges.</li>
          <li>Celebrate wins and progress together.</li>
        </ul>
      </div>
    </div>
  );
}

// Roles & Permissions Guide
function RolesGuide({ userPersona }: { userPersona: UserPersona }) {
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Roles & Permissions</h2>
      <p className="text-muted-foreground">
        Understanding what different roles can do within the platform.
      </p>

      <div className="space-y-4 mt-6">
        <div className="p-4 bg-muted rounded-md">
          <h3 className="text-lg font-medium">Individual Contributors</h3>
          <p className="text-sm text-muted-foreground mb-2">
            Team members focusing on their own work and goals.
          </p>
          <div className="space-y-2">
            <p className="text-sm">
              <strong>Can do:</strong>
            </p>
            <ul className="space-y-1 list-disc pl-5 text-sm">
              <li>Complete daily check-ins</li>
              <li>Chat with team members</li>
              <li>Create and track personal goals</li>
              <li>View team dashboard</li>
              <li>Connect calendar for focus time</li>
              <li>Request help from teammates</li>
            </ul>
          </div>
        </div>

        <div className="p-4 bg-muted rounded-md">
          <h3 className="text-lg font-medium">Team Managers</h3>
          <p className="text-sm text-muted-foreground mb-2">
            Leaders who oversee teams and their wellbeing.
          </p>
          <div className="space-y-2">
            <p className="text-sm">
              <strong>All individual permissions, plus:</strong>
            </p>
            <ul className="space-y-1 list-disc pl-5 text-sm">
              <li>Invite new team members</li>
              <li>View team insights and analytics</li>
              <li>Create team-wide goals</li>
              <li>Organize team activities</li>
              <li>Receive alerts about team wellbeing</li>
              <li>Access team reports and trends</li>
            </ul>
          </div>
        </div>

        <div className="p-4 bg-muted rounded-md">
          <h3 className="text-lg font-medium">Administrators</h3>
          <p className="text-sm text-muted-foreground mb-2">
            System-wide administrators with extensive permissions.
          </p>
          <div className="space-y-2">
            <p className="text-sm">
              <strong>All manager permissions, plus:</strong>
            </p>
            <ul className="space-y-1 list-disc pl-5 text-sm">
              <li>Configure organization-wide settings</li>
              <li>Manage all teams and users</li>
              <li>Access admin dashboard</li>
              <li>Generate organization reports</li>
              <li>Customize platform settings</li>
              <li>Manage integrations and connections</li>
            </ul>
          </div>
        </div>

        <div className="p-4 bg-muted rounded-md">
          <h3 className="text-lg font-medium">New Joiners</h3>
          <p className="text-sm text-muted-foreground mb-2">
            Recently joined team members with an onboarding experience.
          </p>
          <div className="space-y-2">
            <p className="text-sm">
              <strong>Focused on:</strong>
            </p>
            <ul className="space-y-1 list-disc pl-5 text-sm">
              <li>Completing onboarding steps</li>
              <li>Setting up their profile</li>
              <li>Learning platform basics</li>
              <li>Making initial team connections</li>
              <li>Setting first goals</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <p className="text-sm">
          <strong>Your current role: </strong>
          <span className="capitalize">{userPersona}</span>
        </p>
      </div>
    </div>
  );
}

// Calendar Sync Guide
function CalendarGuide() {
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Calendar Sync</h2>
      <p className="text-muted-foreground">
        Connect your calendar to schedule focus time and avoid conflicts.
      </p>

      <div className="space-y-3 mt-4">
        <h3 className="text-lg font-medium">Connecting Your Calendar</h3>
        <ol className="space-y-2 list-decimal pl-5">
          <li>
            Navigate to <strong>Settings</strong> or use any calendar
            integration prompt.
          </li>
          <li>
            Click <strong>Connect Calendar</strong> button.
          </li>
          <li>Choose your calendar provider (Google, Outlook, Apple).</li>
          <li>Authorize the connection when prompted.</li>
          <li>Once connected, your calendar events will be synchronized.</li>
        </ol>
      </div>

      <div className="space-y-3 mt-4">
        <h3 className="text-lg font-medium">Scheduling Focus Time</h3>
        <ol className="space-y-2 list-decimal pl-5">
          <li>
            From the dashboard, click <strong>Schedule Focus Time</strong>.
          </li>
          <li>Select a date and time for your focus session.</li>
          <li>
            The system will check for conflicts with existing calendar events.
          </li>
          <li>Add a title and description for your focus time.</li>
          <li>Set a duration for the session.</li>
          <li>Confirm to add it to your calendar.</li>
        </ol>
      </div>

      <div className="space-y-3 mt-4">
        <h3 className="text-lg font-medium">
          Benefits of Calendar Integration
        </h3>
        <ul className="space-y-2 list-disc pl-5">
          <li>Automatically avoid scheduling conflicts.</li>
          <li>Block dedicated time for focused work.</li>
          <li>Sync your status with your calendar.</li>
          <li>Receive reminders for upcoming focus sessions.</li>
          <li>Let teammates know when you're unavailable.</li>
        </ul>
      </div>

      <div className="p-4 bg-muted rounded-md mt-4">
        <p className="text-sm font-medium">Supported Calendar Providers</p>
        <ul className="space-y-1 list-disc pl-5 text-sm text-muted-foreground">
          <li>Google Calendar</li>
          <li>Microsoft Outlook</li>
          <li>Apple Calendar</li>
        </ul>
      </div>
    </div>
  );
}
